import { ChildProps } from '@apollo/client/react/hoc';
import React from 'react';
import { WithRouterProps } from 'react-router';
import track from 'react-tracking';
import Item2Footer from './item2_footer';
import { Item2Title } from './item2_title';
import { Item2Breadcrumbs } from './item2_breadcrumbs';
import Item2PaidLanding from './item2_paid_landing';
import Item2EndedListingRow, { unqualifiedToRenderEndedListingRow } from './item2_ended_listing_row';
import Item2Pricing, { isLive }  from './item2_pricing';
import Item2ShippingWarning from './item2_shipping_warning';
import { Item2Image } from './item2_image';
import Item2OrderDetails from './item2_order_details';
import { CoreListingLayout } from '@reverbdotcom/commons/src/gql/graphql';

import Item2Metadata from './item2_metadata';
import AppInstallCallout from './app_install_callout';
import Item2ShippingPromptModal from './item2_shipping_prompt_modal';
import { WATCH_PROMPT_PARAM } from './shared/constants';
import { Paths, withWebHost } from './shared/url_helpers';
import {
  Item2NudgeSignals,
} from './item2_nudge_signals';
import { ListingPaymentOptions } from './ListingPaymentOptions';
import { Item2AdWithCspsRowContainer } from './item2_ad_with_csps_row_container';
import { Item2ProductModuleRefreshed } from './item2_product_module_refreshed';
import { Item2StickySidebar } from './item2_sticky_sidebar';
import { Item2StickyBanner } from './item2_sticky_banner';
import { Item2ShopOverview } from './item2_shop_overview';
import { IUserContext, withUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import { isOwner, experimentInt } from '@reverbdotcom/commons/src/user_context_helpers';
import ErrorBoundary from '@reverbdotcom/commons/src/components/error_boundary';
import Item2ImageWatchBadge from './item2_image_watch_badge';
import { ProductReviewsLayout } from './ProductReviewsLayout';
import { fallback, hasUserBoughtItem, showShippingPrompt } from './item2_layout';
import experiments from '@reverbdotcom/commons/src/experiments';
import { Item2AboutThisListing } from './item2_about_this_listing';
import { Item2BuyerProtection } from './item2_buyer_protection';
import { Item2AboutThisSeller } from './item2_about_this_seller';

export type IProps = ChildProps<WithRouterProps & IUserContext, CoreListingLayout.Query, Location>;

const COMPONENT_NAME = 'RefreshedItem2Layout';

export function RefreshedItem2Layout(props: IProps) {
  const { user, data } = props;
  const listingId = props?.data?.listing?.id;
  const location = props?.location;
  const queryParams = location?.query || {};
  const isQualifiedToRenderAdWithCspsRow = !unqualifiedToRenderEndedListingRow(
    props.data?.listing,
    hasUserBoughtItem(props),
    isOwner(user, props.data?.listing),
  );

  const { listing } = data;
  const hasPublishConfirmation = Boolean(queryParams.publish_confirmation);
  const displayShippingPrompt = showShippingPrompt({ user, listing, hasPublishConfirmation });

  const expGroup = experimentInt(user, experiments.WEB_ITEM_PAGE_RESTRUCTURE);
  const isListingLive = isLive(listing);

  return (
    <ErrorBoundary
      fallback={fallback()}
    >
      <AppInstallCallout
        calloutLocation="item"
        canonicalUrl={withWebHost(Paths.item.expand({ id: listingId, bk: queryParams?.bk }))}
      />

      <Item2ShippingPromptModal
        listing={listing}
        showModal={displayShippingPrompt}
      />
      <div className="item2 item2--refreshed">
        <div className="refreshed-item2__site-wrapped-content">
          <Item2Metadata
            listing={listing}
          />
          <div className="dynamic-page dynamic-page--listing">
            {
              hasUserBoughtItem(props) &&
              <Item2OrderDetails
                orders={listing.orders}
              />
            }
            <div className="dynamic-page__content">
              <Item2PaidLanding
                listing={listing}
                location={location}
              />
              <Item2EndedListingRow
                listing={listing}
                similarListings={listing?.similarListings}
                userIsBuyer={hasUserBoughtItem(props)}
                userIsSeller={isOwner(user, listing)}
              />
              <Item2Breadcrumbs
                listing={listing}
              />

              <div className="item2__refreshed-main-content">
                <div className="item2__content__section item2__content__section--image">
                  <div className="item2__image">
                    <Item2Image
                      listing={listing}
                    />
                    <div className="item2__watch-badge">
                      <Item2ImageWatchBadge
                        listing={listing}
                        me={props.data?.me}
                        availableActions={props.data?.me?.availableActions}
                      />
                    </div>
                  </div>
                </div>
                <div className="item2__content__sidebar">
                  <Item2StickySidebar
                    hideSticky
                  >
                    <Item2Title
                      listing={listing}
                    />
                    {isListingLive &&
                      <Item2ShippingWarning
                        listing={listing}
                      />
                    }
                    <Item2Pricing
                      listing={listing}
                      negotiations={props.data.me?.myNegotiations?.negotiations || []}
                      cartItems={props.data.me?.cart?.cartItems || []}
                      userTotalOffersCount={props.data.me?.profile?.totalBuyerOffersCreated}
                      watchPrompt={!!queryParams[WATCH_PROMPT_PARAM]}
                      availableActions={props.data?.me?.availableActions}
                      me={props.data?.me}
                      showKlarnaCallout
                    />
                    <Item2ShopOverview
                      listing={listing}
                    />
                    {isListingLive &&
                      <>
                        <Item2NudgeSignals
                          listing={listing}
                        />
                        <Item2BuyerProtection
                          listing={listing}
                          refreshed
                        />
                      </>
                    }
                    <ListingPaymentOptions listing={listing} />
                  </Item2StickySidebar>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Item2StickyBanner
          listing={listing}
          showBanner={isListingLive && expGroup === 2}
        >
          <div className="refreshed-item2__site-wrapped-content">
            <Item2AboutThisListing
              listing={listing}
              me={props.data?.me}
            />
            <div className="item2__content__section">
              <Item2AboutThisSeller
                listing={listing}
              />
            </div>
            <div>
              <div className="item2__content__section">
                <div>
                  <Item2ProductModuleRefreshed
                    listing={listing}
                  />
                </div>
                <ProductReviewsLayout
                  cspId={listing.csp?.id}
                  trackingDisplayStyle="product"
                  location={location}
                  refreshed
                />
              </div>
            </div>
          </div>
        </Item2StickyBanner>
        <div className="refreshed-item2__site-wrapped-content">
          {
            isQualifiedToRenderAdWithCspsRow &&
            <div className="scaling-mb-6">
              <Item2AdWithCspsRowContainer
                listing={listing}
              />
            </div>
          }
          <Item2Footer listing={listing} />
        </div>
      </div>
    </ErrorBoundary>
  );
}

export default track({ componentName: COMPONENT_NAME })(withUserContext((RefreshedItem2Layout)));
